// src/features/task/TaskTimeTracking.tsx
// Time-tracking section for the task detail page: log time, review the log,
// delete an entry, and see the running total. Wraps LogTime / ListTimeEntries /
// DeleteTimeEntry.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
} from '@react/components';
import { formatDateTime, getDisplayName } from '@/features/task/logic';
import {
  digitsOnly,
  errMsg,
  formatSeconds,
  hoursMinutesToDuration,
  durationToSeconds,
  totalSeconds,
} from '@/features/task/panelHelpers';

interface TaskTimeTrackingProps {
  taskId: string;
  editable?: boolean;
}

export const TaskTimeTracking = observer(({ taskId, editable = true }: TaskTimeTrackingProps) => {
  const store = useTask();
  const { t, locale } = useI18n();

  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await store.ListTimeEntries(taskId);
      const list = res?.entries?.entries ?? res?.entries;
      setEntries(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(errMsg(e, t('task.timeTracking.loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const resetForm = () => {
    setHours('');
    setMinutes('');
    setStartedAt('');
    setDescription('');
    setBillable(false);
  };

  const submit = async () => {
    const h = parseInt(hours || '0', 10);
    const m = parseInt(minutes || '0', 10);
    if (h === 0 && m === 0) {
      setError(t('task.timeTracking.durationRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await store.LogTime(taskId, {
        duration: hoursMinutesToDuration(h, m),
        started_at: startedAt ? new Date(startedAt).toISOString() : new Date().toISOString(),
        description: description.trim() || undefined,
        billable: billable || undefined,
      } as any);
      resetForm();
      setFormOpen(false);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.timeTracking.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entryId: string) => {
    setDeletingId(entryId);
    setError(null);
    try {
      await store.DeleteTimeEntry(taskId, entryId);
      await load();
    } catch (e) {
      setError(errMsg(e, t('task.timeTracking.deleteError')));
    } finally {
      setDeletingId(null);
    }
  };

  const total = totalSeconds(entries);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('task.timeEntries')}</CardTitle>
        <div className="flex items-center gap-2">
          {total > 0 && (
            <Badge variant="secondary" size="sm">
              {t('task.timeTracking.total')}: {formatSeconds(total)}
            </Badge>
          )}
          {editable && !formOpen && (
            <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
              {t('task.timeTracking.logTime')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}

        {formOpen && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex items-center gap-1 text-sm text-foreground">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label={t('task.timeTracking.hours')}
                  placeholder="0"
                  value={hours}
                  onChange={(e) => setHours(digitsOnly(e.target.value))}
                  className="w-14 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                {t('task.timeTracking.hoursShort')}
              </label>
              <label className="flex items-center gap-1 text-sm text-foreground">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label={t('task.timeTracking.minutes')}
                  placeholder="0"
                  value={minutes}
                  onChange={(e) => setMinutes(digitsOnly(e.target.value))}
                  className="w-14 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                {t('task.timeTracking.minutesShort')}
              </label>
              <label className="flex flex-col text-xs text-muted-foreground">
                {t('task.timeTracking.startedAt')}
                <input
                  type="datetime-local"
                  aria-label={t('task.timeTracking.startedAt')}
                  value={startedAt}
                  onChange={(e) => setStartedAt(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
              </label>
            </div>
            <input
              type="text"
              aria-label={t('task.field.description')}
              placeholder={t('task.timeTracking.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
                className="rounded border-input"
              />
              {t('task.timeTracking.billable')}
            </label>
            <div className="flex gap-1.5">
              <Button size="sm" onClick={submit} isLoading={busy}>
                {t('task.timeTracking.save')}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setFormOpen(false); resetForm(); }}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="h-12 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.timeTracking.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {entries.map((entry: any, idx: number) => {
              const entryId = entry.entity?.id || entry.id || '';
              return (
                <div key={entryId || idx} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {formatSeconds(durationToSeconds(entry.duration))}
                      {entry.billable && (
                        <Badge variant="primary" size="sm" className="ml-2">{t('task.billable')}</Badge>
                      )}
                    </p>
                    {entry.description && (
                      <p className="text-sm text-muted-foreground truncate">{entry.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right text-xs text-muted-foreground">
                      <p>{getDisplayName(entry.user)}</p>
                      <p>{formatDateTime(entry.started_at || entry.created_at, locale)}</p>
                    </div>
                    {editable && entryId && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        isLoading={deletingId === entryId}
                        onClick={() => remove(entryId)}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskTimeTracking;
